import { formatCurrency } from '../util/currency'

export type InvoiceRequestEstimatedCostArgs = {
  studentCount: number
  assignmentCount: number
  tokenPrice: number
  sectionPassPriceCents?: number | null
}

/**
 * Whether a product price actually drives the estimate.
 *
 * A non-positive `sectionPassPrice` means the price is not configured rather
 * than free — the same reading as the `sectionPassPrice <= 0` bail in
 * section_section_product_trigger.go — so the token model applies instead.
 * Callers should branch their product-specific UI on this rather than on
 * whether a SectionProduct doc exists, so the rows they render always match
 * the pricing model the estimate used.
 */
export function isInvoiceRequestProductPriced(
  sectionPassPriceCents?: number | null
) {
  return (sectionPassPriceCents ?? 0) > 0
}

/**
 * Source of truth for the estimated cost shown for a section invoice request.
 *
 * Two pricing models apply:
 *
 * - **Section pass (product)** — a product is attached to the section, priced
 *   per participant for the whole section. The number of experiences does not
 *   affect the cost.
 * - **Token** — no product is attached. Priced per student per experience using
 *   the configured `SettingsPricing.tokenPrice`, which is in whole dollars.
 *
 * Every surface that displays an estimated cost (Section page banner, Invoice
 * Request modal, admin/instructor View Request modals, LTI pending request)
 * must go through this so the numbers agree. See BL-4756.
 *
 * @param args.studentCount - number of students on the request
 * @param args.assignmentCount - number of experiences on the request
 * @param args.tokenPrice - `SettingsPricing.tokenPrice`, in whole dollars
 * @param args.sectionPassPriceCents - per-participant price of the product
 *   attached to the section, in cents. Anything non-positive (null/undefined,
 *   or a product whose price is not configured) selects the token pricing
 *   model.
 * @returns the cost in dollars, plus the fraction digits it should be formatted
 *   with (cents are only shown when the price actually has cents)
 */
export function calculateInvoiceRequestEstimatedCost({
  studentCount,
  assignmentCount,
  tokenPrice,
  sectionPassPriceCents,
}: InvoiceRequestEstimatedCostArgs): {
  amount: number
  maximumFractionDigits: number
} {
  const students = Math.max(0, studentCount || 0)

  const priceCents = sectionPassPriceCents ?? 0

  if (isInvoiceRequestProductPriced(priceCents)) {
    // multiply in cents to avoid floating point drift, then convert to dollars
    return {
      amount: (priceCents * students) / 100,
      maximumFractionDigits: priceCents % 100 === 0 ? 0 : 2,
    }
  }

  const assignments = Math.max(0, assignmentCount || 0)

  return {
    amount: students * assignments * Math.max(0, tokenPrice || 0),
    maximumFractionDigits: 0,
  }
}

/**
 * `calculateInvoiceRequestEstimatedCost` rendered as a USD string. Use this
 * wherever an estimated cost is displayed.
 */
export function formatInvoiceRequestEstimatedCost(
  args: InvoiceRequestEstimatedCostArgs
) {
  const { amount, maximumFractionDigits } =
    calculateInvoiceRequestEstimatedCost(args)

  return formatCurrency(amount, 'USD', { maximumFractionDigits })
}
